import React from "react";
// Re-exported below so existing importers (`import { formatSourceUrl } from
// "./ContentSourcePicker"`) keep working; the implementation now lives in a
// shared, dependency-free util so SiteCaptureModal can use it without a
// circular import.
import { formatSourceUrl } from "../../../utilities/formatSourceUrl";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import CheckIcon from "@mui/icons-material/Check";

import LinkIcon from "@mui/icons-material/Link";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { toast } from "react-toastify";

import {
  getRightworkAPI,
  postRightworkAPI,
  getDefaultAxiosConfig,
} from "../requests/utilities/requests";
import {
  API_get_assistant_searchable_files,
  API_post_file_upload_with_eagerly_generated_presigned_url,
  API_notify_file_upload_complete,
  API_post_resolve_ai_sources_folder,
  API_get_file_upload_indexing_status,
} from "../../../constants";
import SiteCaptureModal from "../documents/SiteCaptureModal";
import { isVideoFile } from "./swipeDeckHelpers";

// Extensions worth offering as generation sources — mirrors the
// backend's INDEXABLE_EXTENSIONS plus video (transcribed, then chunked the
// same way). Anything else uploads fine but never produces grounding chunks.
const UPLOAD_ACCEPT =
  ".pdf,.docx,.pptx,.txt,.md,.csv,.json,.png,.jpg,.jpeg,.webp," +
  ".mp4,.mov,.m4v,.webm";
// Indexing poll cadence + give-up horizon. Vision-OCR'd scans can take
// minutes; past the horizon the row stops claiming progress and tells
// the manager the file will be selectable later (it lands in the
// document list once indexed — nothing is lost).
const INDEX_POLL_MS = 4000;
const INDEX_POLL_MAX_MS = 5 * 60 * 1000;
// Videos are transcribed on a background worker and the backend accepts them
// up to ~60 min, so they legitimately take far longer than docs to become
// selectable. Give video rows a much longer horizon so a valid long upload is
// still auto-selected / refreshed in while the modal stays open, rather than
// being flipped to "slow" at five minutes and abandoned. (Nothing is lost
// either way — the periodic sweep indexes it — but this keeps the live UX.)
const INDEX_POLL_MAX_MS_VIDEO = 60 * 60 * 1000;

/**
 * THE source picker for AI generation — used by the training studio and
 * the quiz + swipe-deck generators. One component so source types (and
 * future import flows like add-from-link) land everywhere at once
 * instead of drifting across parallel pickers.
 *
 * Source types, gated per consumer:
 *   - Knowledge-hub documents (always). Self-fetched unless the caller
 *     injects `files` (the studio fetches once at mount because it also
 *     needs the list for chips + folder→file expansion outside the
 *     picker).
 *   - Whole folders (`showFolders` — studio only; selection expands to
 *     file uuids at submit time in the caller).
 *   - Existing LMS modules + sections (always; callers pass the lists).
 *   - Pasted text (`showPaste` — generators only; the studio grounds on
 *     its prompt instead).
 *
 * Selection state lives in the PARENT so the modal can close/reopen
 * without losing picks. Toggles fire synchronously — no commit-on-Done
 * step. `onToggleFile(uuid, name)` carries the name so callers can
 * label chips without re-fetching.
 */
export default function ContentSourcePicker({
  open,
  onClose,
  companyId,
  title = "Add reference content",
  // Data. `files`/`folders` are optional — omit `files` to have the
  // picker fetch assistant-searchable documents itself.
  files: injectedFiles,
  folders,
  modules,
  sections,
  // Selection (uuid arrays).
  selectedFileUuids,
  selectedFolderUuids,
  selectedModuleUuids,
  selectedSectionUuids,
  pastedText,
  // Toggles.
  onToggleFile,
  onToggleFolder,
  onToggleModule,
  onToggleSection,
  onPastedTextChange,
  // Injected-files mode only: re-run the caller's file fetch after an
  // in-picker import (self-fetch mode refreshes itself).
  onRefreshFiles,
  // Feature flags.
  showFolders = false,
  showPaste = false,
}) {
  const [query, setQuery] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const searchInputRef = React.useRef(null);

  const selfFetch = injectedFiles === undefined;
  const [fetchedFiles, setFetchedFiles] = React.useState([]);
  const [filesLoading, setFilesLoading] = React.useState(false);
  const [filesError, setFilesError] = React.useState(false);
  const files = selfFetch ? fetchedFiles : injectedFiles || [];
  const [captureOpen, setCaptureOpen] = React.useState(false);
  // Bumped after an in-picker import to re-run the self-fetch.
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  // In-flight picker uploads: [{uuid, name, status}] where status is
  // uploading | indexing | no_text | failed | slow. Ready rows are
  // auto-selected and removed (the refreshed document list takes over).
  const [pendingUploads, setPendingUploads] = React.useState([]);
  const uploadInputRef = React.useRef(null);
  const pollStartRef = React.useRef(null);

  // Reset query + filter on open so the picker starts fresh each time.
  React.useEffect(() => {
    if (open) {
      setQuery("");
      setTypeFilter("all");
      // Defer focus past the MUI Modal focus trap.
      const t = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Self-fetch path: load searchable documents when the picker opens.
  // Failure is non-fatal — other source types still work.
  React.useEffect(() => {
    if (!selfFetch || !open || !companyId) return;
    let cancelled = false;
    async function loadFiles() {
      // Clear BEFORE fetching: a companyId change (or a permissions
      // change) must never leave the previous scope's documents
      // renderable/selectable while the new request is in flight — and
      // especially not after a FAILED refetch, where stale rows would
      // expose another scope's file names and create selections the
      // backend's gates silently drop from the context.
      setFetchedFiles([]);
      setFilesLoading(true);
      setFilesError(false);
      try {
        const res = await getRightworkAPI(API_get_assistant_searchable_files, {
          company_id: companyId,
        });
        if (cancelled) return;
        setFetchedFiles(res.data?.redux?.payload?.files || []);
      } catch (err) {
        // fetchedFiles stays [] from the pre-fetch clear — the failure
        // path shows the retry hint against an empty list, never stale rows.
        if (!cancelled) setFilesError(true);
      } finally {
        if (!cancelled) setFilesLoading(false);
      }
    }
    loadFiles();
    return () => {
      cancelled = true;
    };
  }, [selfFetch, open, companyId, refreshNonce]);

  const fileUuidSet = React.useMemo(
    () => new Set(selectedFileUuids || []),
    [selectedFileUuids],
  );
  const folderUuidSet = React.useMemo(
    () => new Set(selectedFolderUuids || []),
    [selectedFolderUuids],
  );
  const moduleUuidSet = React.useMemo(
    () => new Set(selectedModuleUuids || []),
    [selectedModuleUuids],
  );
  const sectionUuidSet = React.useMemo(
    () => new Set(selectedSectionUuids || []),
    [selectedSectionUuids],
  );

  const typeFilters = [
    { key: "all", label: "All" },
    ...(showFolders ? [{ key: "folder", label: "Folders" }] : []),
    { key: "document", label: "Documents" },
    { key: "module", label: "Modules" },
    { key: "section", label: "Sections" },
    ...(showPaste ? [{ key: "text", label: "Paste text" }] : []),
  ];

  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (name, secondary) => {
    if (!normalizedQuery) return true;
    if (name && name.toLowerCase().includes(normalizedQuery)) return true;
    if (secondary && secondary.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    return false;
  };

  const showFolderRows =
    showFolders && (typeFilter === "all" || typeFilter === "folder");
  const showModuleRows = typeFilter === "all" || typeFilter === "module";
  const showSectionRows = typeFilter === "all" || typeFilter === "section";
  const showDocumentRows = typeFilter === "all" || typeFilter === "document";
  const showPastePanel =
    showPaste && (typeFilter === "all" || typeFilter === "text");

  const visibleFolders = showFolderRows
    ? (folders || []).filter((f) => matchesQuery(f.name))
    : [];
  const visibleModules = showModuleRows
    ? (modules || []).filter((m) => matchesQuery(m.name, m.category))
    : [];
  const visibleSections = showSectionRows
    ? (sections || []).filter((s) => matchesQuery(s.name, s.category))
    : [];
  const visibleFiles = showDocumentRows
    ? (files || []).filter((f) =>
        // Match on the displayed label too: URL-ingested rows show their
        // source URL, so searching that text must hit (otherwise they're
        // unsearchable by what's on screen — only by the hidden .md name).
        matchesQuery(f.name, f.folder_name) ||
        matchesQuery(formatSourceUrl(f.source_url)))
    : [];

  const refreshDocuments = React.useCallback(() => {
    if (selfFetch) {
      setRefreshNonce((n) => n + 1);
    } else {
      onRefreshFiles?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfFetch, onRefreshFiles]);

  // Picker-side upload: standard presigned flow into the managed
  // AI-sources folder, then poll for indexing readiness. Unlike URL
  // imports (which chunk-index inline), uploads index async — a big or
  // scanned PDF takes seconds to minutes — so files are NOT selectable
  // until their chunks exist; auto-selecting an unindexed file would
  // silently contribute nothing to generation.
  const handleUploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    let folderUuid;
    try {
      const res = await postRightworkAPI(API_post_resolve_ai_sources_folder, {
        company_id: companyId,
      });
      folderUuid = res?.data?.redux?.payload?.folder?.uuid;
      if (!folderUuid) throw new Error("No folder returned");
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          "Couldn't prepare the uploads folder. Please try again.",
        { autoClose: 4000 },
      );
      return;
    }

    pollStartRef.current = Date.now();
    for (const [fileIndex, file] of files.entries()) {
      // Index makes the key unique even for same-name-same-size picks —
      // a collision would make both rows mirror one file's status.
      const tempKey = `pending-${fileIndex}-${file.name}-${file.size}`;
      // Reference videos are transcribed server-side (60-min ceiling); cap
      // the upload so a multi-GB file isn't pushed only to fail downstream.
      // Detect by extension too — browsers report empty File.type for .mov.
      if (isVideoFile(file) && file.size > 500 * 1024 * 1024) {
        toast.error(
          `"${file.name}" is over 500MB. Trim or compress it first.`,
          { autoClose: 5000 },
        );
        continue;
      }
      setPendingUploads((prev) => [
        ...prev,
        // isVideo drives the longer readiness-poll horizon below.
        { uuid: tempKey, name: file.name, status: "uploading", isVideo: isVideoFile(file) },
      ]);
      try {
        // Same 3-step flow as the Knowledge Hub's upload_file.
        const formData = new FormData();
        formData.append("company_id", companyId);
        formData.append("file_name", file.name);
        formData.append("type", "file");
        formData.append("file_upload_folder_uuid", folderUuid);
        const axiosConfig = await getDefaultAxiosConfig();
        const res = await axios.post(
          API_post_file_upload_with_eagerly_generated_presigned_url,
          formData,
          {
            headers: {
              ...axiosConfig.headers,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        const fileUuid = res?.data?.redux?.payload?.uuid;
        const presignedUrl = res?.data?.redux?.payload?.presigned_url;
        if (!fileUuid || !presignedUrl) throw new Error("Upload setup failed");
        await axios.put(presignedUrl, file, {
          headers: { "Content-Type": file.type },
        });
        await postRightworkAPI(API_notify_file_upload_complete, {
          file_upload_uuid: fileUuid,
          company_id: companyId,
        });
        setPendingUploads((prev) =>
          prev.map((u) =>
            u.uuid === tempKey
              // Spread to preserve isVideo (and name) — replacing the object
              // would drop the flag the poll horizon relies on.
              ? { ...u, uuid: fileUuid, status: "indexing" }
              : u,
          ),
        );
      } catch (err) {
        setPendingUploads((prev) =>
          prev.map((u) =>
            u.uuid === tempKey ? { ...u, status: "failed" } : u,
          ),
        );
      }
    }
    refreshDocuments();
  };

  // Readiness poll — runs only while something is indexing and the
  // picker is open. Ready files are auto-selected (same contract as
  // link imports) and leave the pending list; the refreshed document
  // list carries them from there.
  React.useEffect(() => {
    if (!open) return;
    const indexing = pendingUploads.filter((u) => u.status === "indexing");
    if (indexing.length === 0) return;
    const timer = setInterval(async () => {
      // Per-row give-up horizon: videos get a much longer window than docs, so
      // a long reference video that takes more than five minutes keeps polling
      // (and gets auto-selected on ready) instead of being flipped to "slow".
      // Only rows past THEIR own horizon flip; the rest keep polling, and the
      // effect re-runs (pendingUploads changed) to narrow the polled set.
      const elapsed = pollStartRef.current
        ? Date.now() - pollStartRef.current
        : 0;
      const rowHorizonMs = (u) =>
        u.isVideo ? INDEX_POLL_MAX_MS_VIDEO : INDEX_POLL_MAX_MS;
      // Only touch state when a row has actually crossed its horizon, so a
      // normal tick doesn't allocate a new array / trigger a re-render.
      if (indexing.some((u) => elapsed > rowHorizonMs(u))) {
        setPendingUploads((prev) =>
          prev.map((u) =>
            u.status === "indexing" && elapsed > rowHorizonMs(u)
              ? { ...u, status: "slow" }
              : u,
          ),
        );
      }
      try {
        const res = await getRightworkAPI(API_get_file_upload_indexing_status, {
          company_id: companyId,
          file_upload_uuids: indexing.map((u) => u.uuid).join(","),
        });
        const statuses = res?.data?.redux?.payload?.statuses || {};
        const becameReady = indexing.filter(
          (u) => statuses[u.uuid] === "ready",
        );
        becameReady.forEach((u) => {
          if (!fileUuidSet.has(u.uuid)) onToggleFile?.(u.uuid, u.name);
        });
        if (becameReady.length > 0) refreshDocuments();
        setPendingUploads((prev) =>
          prev
            // Surface terminal non-ready states. "failed" covers video
            // transcription failures (no audio, over the duration limit);
            // without this the row stays "indexing" and times out to "slow".
            .map((u) =>
              statuses[u.uuid] === "no_text" || statuses[u.uuid] === "failed"
                ? { ...u, status: statuses[u.uuid] }
                : u,
            )
            .filter((u) => statuses[u.uuid] !== "ready"),
        );
      } catch (err) {
        // Transient poll failure — keep trying until the horizon.
      }
    }, INDEX_POLL_MS);
    return () => clearInterval(timer);
    // selectedFileUuids is a dep so the auto-select's already-selected
    // check can't read a stale set: once the list refreshes, a pending
    // file is manually selectable, and a stale check would toggle a
    // manual selection OFF when readiness lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingUploads, companyId, selectedFileUuids]);

  // In-picker site capture: imported pages land in the auto-managed
  // "Imported pages" folder (assistant-searchable by construction), the
  // document list refreshes, and each imported file is auto-selected so
  // the manager's next click is Generate, not a re-find.
  const handleCaptureImported = (imported) => {
    refreshDocuments();
    (imported || []).forEach((f) => {
      if (f.uuid && !fileUuidSet.has(f.uuid)) {
        onToggleFile?.(f.uuid, f.name);
      }
    });
  };

  const pastedLen = (pastedText || "").trim().length;
  const totalSelected =
    fileUuidSet.size +
    folderUuidSet.size +
    moduleUuidSet.size +
    sectionUuidSet.size +
    (showPaste && pastedLen > 0 ? 1 : 0);

  // Selected items resolved to removable chips for the sticky summary bar,
  // so it's always clear what's getting included as the manager scrolls the
  // list. Each carries its own remove handler. Lists are small (a selection,
  // not the whole library), so per-chip .find is fine.
  const selectedChips = [];
  (selectedFolderUuids || []).forEach((u) => {
    const f = (folders || []).find((x) => x.uuid === u);
    if (f) {
      selectedChips.push({
        key: `fld-${u}`, label: f.name, Icon: FolderOutlinedIcon,
        cls: "bg-amber-50 border-amber-200 text-amber-900", iconCls: "text-amber-600",
        onRemove: () => onToggleFolder?.(u),
      });
    }
  });
  (selectedModuleUuids || []).forEach((u) => {
    const m = (modules || []).find((x) => x.uuid === u);
    if (m) {
      selectedChips.push({
        key: `mod-${u}`, label: m.name, Icon: ViewModuleIcon,
        cls: "bg-violet-50 border-violet-200 text-violet-900", iconCls: "text-violet-600",
        onRemove: () => onToggleModule?.(u),
      });
    }
  });
  (selectedSectionUuids || []).forEach((u) => {
    const s = (sections || []).find((x) => x.uuid === u);
    if (s) {
      selectedChips.push({
        key: `sec-${u}`, label: s.name, Icon: ViewHeadlineIcon,
        cls: "bg-white border-slate-200 text-slate-700", iconCls: "text-slate-500",
        onRemove: () => onToggleSection?.(u),
      });
    }
  });
  (selectedFileUuids || []).forEach((u) => {
    const f = (files || []).find((x) => x.uuid === u);
    const url = f ? formatSourceUrl(f.source_url) : null;
    selectedChips.push({
      key: `f-${u}`, label: url || f?.name || "Document",
      Icon: url ? LinkIcon : DescriptionOutlinedIcon,
      cls: "bg-sky-50 border-sky-200 text-sky-900", iconCls: "text-sky-600",
      onRemove: () => onToggleFile?.(u),
    });
  });
  if (showPaste && pastedLen > 0) {
    selectedChips.push({
      key: "paste", label: "Pasted text", Icon: NotesIcon,
      cls: "bg-amber-50 border-amber-200 text-amber-900", iconCls: "text-amber-600",
      onRemove: () => onPastedTextChange?.(""),
    });
  }
  const totalVisibleRows =
    visibleFolders.length +
    visibleModules.length +
    visibleSections.length +
    visibleFiles.length;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="source-picker-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(720px, 92vw)",
          maxHeight: "min(80vh, 720px)",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2
            id="source-picker-title"
            className="text-base font-semibold text-slate-900"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Search + filter pills */}
        <div className="px-5 pt-4 pb-3 space-y-3 border-b border-slate-200">
          {typeFilter !== "text" && (
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                style={{ fontSize: 18 }}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search your sources…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-violet-500 focus:bg-white"
              />
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {typeFilters.map((t) => {
              const active = typeFilter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTypeFilter(t.key)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    active
                      ? "bg-violet-700 text-white border-violet-700"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
            <button
              onClick={() => setCaptureOpen(true)}
              className="ml-auto px-3 py-1 text-xs font-medium rounded-full border border-violet-200 text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1"
            >
              <LinkIcon style={{ fontSize: 13 }} />
              Add from link
            </button>
            <button
              onClick={() => uploadInputRef.current?.click()}
              className="px-3 py-1 text-xs font-medium rounded-full border border-violet-200 text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1"
            >
              <UploadFileIcon style={{ fontSize: 13 }} />
              Upload
            </button>
            <input
              ref={uploadInputRef}
              type="file"
              multiple
              accept={UPLOAD_ACCEPT}
              className="hidden"
              onChange={handleUploadFiles}
            />
          </div>
        </div>

        {/* Sticky selected-summary — stays between the (fixed) filters and
            the scrollable list so it's always clear what's getting included
            as the manager scrolls. Each chip removes its own selection. */}
        {selectedChips.length > 0 && (
          <div className="flex-shrink-0 px-5 py-2.5 border-b border-slate-200 bg-slate-50">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Selected ({selectedChips.length})
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {selectedChips.map((c) => {
                const Icon = c.Icon;
                return (
                  <span
                    key={c.key}
                    className={`inline-flex items-center gap-1 text-xs border rounded px-2 py-1 ${c.cls}`}
                  >
                    <Icon style={{ fontSize: 13 }} className={c.iconCls} />
                    <span className="font-medium max-w-[220px] truncate">{c.label}</span>
                    <button
                      onClick={c.onRemove}
                      className="ml-0.5 opacity-70 hover:opacity-100"
                      aria-label={`Remove ${c.label}`}
                    >
                      <CloseIcon style={{ fontSize: 12 }} />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Result list */}
        <div className="flex-1 overflow-y-auto">
          {pendingUploads.length > 0 && (
            <div className="divide-y divide-slate-100 border-b border-slate-200 bg-slate-50/60">
              {pendingUploads.map((u) => (
                <div
                  key={u.uuid}
                  className="px-5 py-2.5 flex items-center gap-3"
                >
                  {u.status === "uploading" || u.status === "indexing" ? (
                    <CircularProgress size={16} sx={{ color: "#6d28d9" }} />
                  ) : (
                    <span className="w-4" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-700 truncate">
                      {u.name}
                    </div>
                    <div
                      className={`text-xs ${
                        u.status === "failed" || u.status === "no_text"
                          ? "text-rose-600"
                          : "text-slate-500"
                      }`}
                    >
                      {u.status === "uploading" && "Uploading…"}
                      {u.status === "indexing" &&
                        "Processing for AI use (videos are transcribed) — selectable when ready…"}
                      {u.status === "slow" &&
                        "Still processing — it will appear in Documents once ready."}
                      {u.status === "no_text" &&
                        "No readable text found — this file can't ground generation."}
                      {u.status === "failed" &&
                        "Couldn't process this file. Please try again."}
                    </div>
                  </div>
                  {(u.status === "failed" ||
                    u.status === "no_text" ||
                    u.status === "slow") && (
                    <button
                      onClick={() =>
                        setPendingUploads((prev) =>
                          prev.filter((x) => x.uuid !== u.uuid),
                        )
                      }
                      className="text-xs text-slate-400 hover:text-slate-600"
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Paste-text panel — its own filter, or part of "All" */}
          {showPastePanel && (
            <div className="px-5 py-4 border-b border-slate-100">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                <NotesIcon style={{ fontSize: 18 }} className="text-slate-500" />
                Paste reference text
              </label>
              <textarea
                rows={typeFilter === "text" ? 10 : 4}
                value={pastedText || ""}
                onChange={(e) => onPastedTextChange?.(e.target.value)}
                placeholder="Paste a policy, recipe, SOP, or any text you want the AI to use as a source…"
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-violet-500 focus:bg-white resize-y"
              />
            </div>
          )}

          {/* Document loading / error states (self-fetch path only) */}
          {showDocumentRows && filesLoading && (
            <div className="px-5 py-4 text-sm text-slate-500">
              Loading documents…
            </div>
          )}
          {showDocumentRows && filesError && !filesLoading && (
            <div className="px-5 py-4 text-sm text-rose-600">
              Couldn't load documents. Close and reopen to retry.
            </div>
          )}

          {totalVisibleRows === 0 && !showPastePanel && !filesLoading ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">
              {normalizedQuery
                ? "No matches for that search."
                : "Nothing available here yet. Upload documents to folders with assistant search enabled, or create LMS content to reuse."}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {visibleFolders.map((folder) => {
                const checked = folderUuidSet.has(folder.uuid);
                return (
                  <PickerRow
                    key={`folder-${folder.uuid}`}
                    checked={checked}
                    onClick={() => onToggleFolder?.(folder.uuid)}
                    icon={
                      <FolderOutlinedIcon
                        className="text-amber-600 flex-shrink-0"
                        style={{ fontSize: 20 }}
                      />
                    }
                    title={folder.name}
                    subtitle={`${folder.file_count} file${
                      folder.file_count === 1 ? "" : "s"
                    }`}
                  />
                );
              })}
              {visibleModules.map((module) => {
                const checked = moduleUuidSet.has(module.uuid);
                return (
                  <PickerRow
                    key={`module-${module.uuid}`}
                    checked={checked}
                    onClick={() => onToggleModule?.(module.uuid)}
                    icon={
                      <ViewModuleIcon
                        className="text-violet-600 flex-shrink-0"
                        style={{ fontSize: 20 }}
                      />
                    }
                    title={module.name}
                    subtitle={module.category}
                  />
                );
              })}
              {visibleSections.map((section) => {
                const checked = sectionUuidSet.has(section.uuid);
                return (
                  <PickerRow
                    key={`section-${section.uuid}`}
                    checked={checked}
                    onClick={() => onToggleSection?.(section.uuid)}
                    icon={
                      <ViewHeadlineIcon
                        className="text-slate-500 flex-shrink-0"
                        style={{ fontSize: 20 }}
                      />
                    }
                    title={section.name}
                    subtitle={section.category}
                  />
                );
              })}
              {visibleFiles.map((file) => {
                const checked = fileUuidSet.has(file.uuid);
                const sourceUrl = formatSourceUrl(file.source_url);
                return (
                  <PickerRow
                    key={`file-${file.uuid}`}
                    checked={checked}
                    // Name + source_url ride along so callers' chips can
                    // render exactly like the AI studio's chips (label by
                    // URL when URL-ingested, filename otherwise, with the
                    // matching icon) without re-fetching the file list.
                    onClick={() =>
                      onToggleFile?.(file.uuid, file.name, file.source_url)
                    }
                    icon={
                      sourceUrl ? (
                        <LinkIcon
                          className="text-sky-600 flex-shrink-0"
                          style={{ fontSize: 20 }}
                        />
                      ) : (
                        <DescriptionOutlinedIcon
                          className="text-sky-600 flex-shrink-0"
                          style={{ fontSize: 20 }}
                        />
                      )
                    }
                    // URL-ingested files read as the page they came from;
                    // normal uploads keep their filename.
                    title={sourceUrl || file.name}
                    subtitle={file.folder_name}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200">
          <div className="text-xs text-slate-600">
            {totalSelected === 0
              ? "Nothing selected"
              : `${totalSelected} source${totalSelected === 1 ? "" : "s"} selected`}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg font-medium text-sm"
          >
            Done
          </button>
        </div>

        {/* Nested site-capture: discover + pick + import pages from a
            URL without leaving the picker. */}
        <SiteCaptureModal
          open={captureOpen}
          onClose={() => setCaptureOpen(false)}
          companyId={companyId}
          useAiSourcesFolder
          onImported={handleCaptureImported}
        />
      </Box>
    </Modal>
  );
}

// URL-ingested files (site capture / add-from-link) carry a source_url; we
// label those by the URL rather than the derived ".md" filename so the row
// matches what the manager added. Strips protocol + "www." + trailing slash.
// Re-exported from the shared util (imported at the top) so existing callers
// that do `import { formatSourceUrl } from "./ContentSourcePicker"` keep working.
export { formatSourceUrl };

function PickerRow({ checked, onClick, icon, title, subtitle }) {
  return (
    <button
      onClick={onClick}
      className={`w-full px-5 py-3 flex items-center gap-3 hover:bg-slate-50 text-left ${
        checked ? "bg-violet-50" : ""
      }`}
    >
      <Checkbox checked={checked} />
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate">
          {title}
        </div>
        {subtitle && (
          <div className="text-xs text-slate-500 truncate">{subtitle}</div>
        )}
      </div>
    </button>
  );
}

/**
 * Tiny inline checkbox — explicitly NOT MUI Checkbox to keep the
 * picker rows visually quiet (MUI's default state styling is loud
 * for a row that already has its own selected-bg treatment).
 */
function Checkbox({ checked }) {
  return (
    <span
      className={`flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center ${
        checked
          ? "bg-violet-700 border-violet-700"
          : "bg-white border-slate-300"
      }`}
    >
      {checked && <CheckIcon style={{ fontSize: 14, color: "white" }} />}
    </span>
  );
}
